/**
 * Componente SimpleImageViewer - Visor de imágenes simple
 * Muestra imágenes en pantalla completa con navegación básica
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const SimpleImageViewer = ({ 
  images = [], 
  isOpen = false, 
  onClose, 
  initialIndex = 0,
  title = "Galería de Imágenes",
  style = {}
}) => {
  // Detectar si se requiere fondo sólido
  const useSolidBackground = style.backdropFilter === 'none';
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  // Estados para gestos de swipe en móvil
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Mínima distancia para considerar un swipe
  const minSwipeDistance = 50;

  // Resetear índice cuando se abre el visor
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev < images.length - 1) {
        return prev + 1;
      }
      return prev;
    });
  }, [images.length]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev > 0) {
        return prev - 1;
      }
      return prev;
    });
  }, []);

  // Cerrar con ESC y navegación con teclas
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, handleNext, handlePrevious]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Manejar gestos de swipe para móvil
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      handleNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
  };

  if (!isOpen || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];

  const viewerContent = (
    <div 
      className="fixed inset-0 bg-black flex items-center justify-center"
      style={{ 
        zIndex: 9999, 
        width: '100vw', 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        ...style 
      }}
      onClick={handleBackdropClick}
    >
      {/* Header */}
      <div className={`absolute top-0 left-0 right-0 z-10 p-4 ${
        useSolidBackground 
          ? 'bg-gray-900' 
          : 'bg-gradient-to-b from-black/95 to-transparent'
      }`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
            <h3 className="text-base sm:text-lg font-medium truncate">{title}</h3>
            <span className="text-sm text-gray-300 mt-1 sm:mt-0">
              {currentIndex + 1} de {images.length}
            </span>
          </div>
          
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
            title="Cerrar (ESC)"
          >
            <FaTimes size={18} />
          </button>
        </div>
      </div>

      {/* Imagen principal */}
      <div 
        className="relative w-full h-full flex items-center justify-center p-4 pt-20 pb-24"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentImage.url_storage || currentImage.url}
          alt={currentImage.descripcion || `Imagen ${currentIndex + 1}`}
          className="w-full h-full object-contain select-none"
          style={{ maxWidth: '100vw', maxHeight: '100vh' }}
          draggable={false}
        />

        {/* Navegación - Anterior */}
        {images.length > 1 && currentIndex > 0 && (
          <button
            type="button"
            onClick={handlePrevious}
            className="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-lg"
            title="Imagen anterior (←)"
          >
            <FaChevronLeft size={24} />
          </button>
        )}

        {/* Navegación - Siguiente */}
        {images.length > 1 && currentIndex < images.length - 1 && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-lg"
            title="Siguiente imagen (→)"
          >
            <FaChevronRight size={24} />
          </button>
        )}
      </div>

      {/* Indicadores de posición (dots) */}
      {images.length > 1 && (
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 px-3 py-2 rounded-full flex space-x-2 ${
          useSolidBackground 
            ? 'bg-black/95' 
            : 'bg-black/60 backdrop-blur-sm'
        }`}>
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex 
                  ? 'bg-white' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              title={`Ir a imagen ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Footer con información */}
      {(currentImage?.descripcion || currentImage?.nombre_archivo) && (
        <div className={`absolute bottom-0 left-0 right-0 z-10 p-4 pb-6 ${
          useSolidBackground 
            ? 'bg-gray-900' 
            : 'bg-gradient-to-t from-black/95 to-transparent'
        }`}>
          <div className="text-center text-white">
            <p className="text-sm text-gray-300">
              {currentImage.descripcion || 'Sin descripción'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Usar createPortal para renderizar fuera del DOM del componente padre
  return createPortal(viewerContent, document.body);
};

export default SimpleImageViewer;
export { SimpleImageViewer as ImageViewer };